import { DateFormatter } from '@repo/toolkit/date'
import { useNavigator } from '@repo/toolkit/use-navigator'
import { Column } from '@repo/ui/data-table'
import { EuroIcon, PlusIcon } from '@repo/ui/icons'
import React, { useState } from 'react'

import { API } from '../../api/apis.config'
import CountryFlag from '../../components/elements/country-flag'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import PeriodBadge from '../../components/elements/period-badge'
import { PaymentConditionFilters } from '../../components/pages/payment-condition/payment-condition-filters'
import { Paths } from '../../lib/constants/path'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import { depositAmountTypeEnumMapping } from '../../lib/types/enums/payment-condition/deposit-amount-type.enum'
import { PaymentCondition } from '../../lib/types/models/payment-condition/payment-condition'
import { PaymentConditionFilter } from '../../lib/types/models/payment-condition/payment-condition-filter'
import {
  transformPaymentConditionFiltersToUrl,
  transformUrlToPaymentConditionFilters
} from '../../lib/utils/filters-mapping/payment-conditions'

const PaymentConditionListPage = () => {
  const PaymentConditionInitialFilter: PaymentConditionFilter = {}
  const { navigate, navigateWithEvent } = useNavigator()
  const tableRef = React.useRef<DynamicTableRef<PaymentCondition>>(null)
  const [isTableLoading, setIsTableLoading] = useState(false)

  const { modalState, toggleModal, closeModel } = useModalManager<'filters', PaymentCondition[]>([
    'filters'
  ])

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<PaymentConditionFilter>({
      initialFilters: PaymentConditionInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformPaymentConditionFiltersToUrl,
      transformUrlToFilter: transformUrlToPaymentConditionFilters
    })

  const columns: Column<PaymentCondition>[] = [
    {
      accessorKey: 'depositType',
      header: 'Type',
      enableSorting: true,
      cell: ({ row }) => depositAmountTypeEnumMapping[row.original.depositType]
    },
    {
      accessorKey: 'depositPercentageOrAmountWithoutInsurance',
      header: "Pourcentage ou montant de l'acompte (hors assurance)",
      enableSorting: true
    },
    {
      accessorKey: 'secondPaymentDaysAfterBulletinValidation',
      header: '# jours après validation du bulletin pour lla 2e échéance de paiement',
      enableSorting: true
    },
    {
      accessorKey: 'secondPaymentPercentageWithoutInsurance',
      header: 'Pourcentage pour le 2e paiement',
      enableSorting: true
    },
    {
      accessorKey: 'balancePaymentDaysBeforeDeparture',
      header: '# Jours avant le départ pour le paiement du solde',
      enableSorting: true,
      cell: ({ row }) => row.original.balancePaymentDaysBeforeDeparture ?? '-'
    },
    {
      accessorKey: 'balancePaymentDeadline',
      header: 'Date limite de paiement du solde',
      enableSorting: true,
      cell: ({ row }) =>
        row.original.balancePaymentDeadline
          ? DateFormatter.DDMMYYYY(row.original.balancePaymentDeadline)
          : '-'
    },
    {
      accessorKey: 'countries',
      header: 'Pays',
      columnHeaderClassName: 'w-1/12',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-2 ">
          {row.original.countries?.map((country) => (
            <CountryFlag key={country.id} code={country.code} />
          ))}
        </div>
      )
    },
    {
      accessorKey: 'periods',
      header: 'Périodes',
      cell: ({ row }) => (
        <div className="flex flex-wrap gap-2 py-1">
          {row.original.periods?.map((period) => (
            <PeriodBadge key={period.id} period={period} />
          ))}
        </div>
      )
    }
  ]

  return (
    <div>
      <UserNavBar title={'Modalités de paiement'} icon={EuroIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<PaymentCondition, PaymentConditionFilter>
        ref={tableRef}
        onLoadingChange={setIsTableLoading}
        title={'Modalités de paiement'}
        deleteMessage={'Êtes-vous sûr de vouloir supprimer cette condition de paiement?'}
        columns={columns}
        isSearchable={false}
        selectable
        allowSearchParam
        onRowClick={(paymentCondition, event) =>
          navigateWithEvent(
            Paths.PAYMENT_CONDITIONS_UPDATE_PAGE.replace(':id', paymentCondition.id.toString()),
            event
          )
        }
        endpoint={API.paymentConditions.getAll.url}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            refetch: true,
            variant: 'primary',
            label: 'Ajouter condition de paiement',
            fn: () => navigate(Paths.PAYMENT_CONDITIONS_CREATE_PAGE)
          }
        ]}
      />

      <FilterDrawer
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <PaymentConditionFilters
          filter={filter}
          onFilterChange={(filter) => onFilterChange(filter)}
        />
      </FilterDrawer>
    </div>
  )
}

export default PaymentConditionListPage
